import {
  Analytics,
  Brightness6,
  CloudUpload,
  CropOriginal,
  InstallDesktop,
  Layers,
  LiveTv,
  OfflineBolt,
  PlayArrow,
  Send,
  SettingsRemote,
  StopCircle,
  SwapHoriz,
  VolumeUp,
} from '@mui/icons-material';
import { Button } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSendDeviceCommand } from '~/api/devices';
import { TransferDialog } from '~/components/devices/TransferDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { SettingsBody, SettingsData, SettingsHead } from '~/components/settings';
import { VerticalButton } from '~/components/vertical-button';
import { useAppContext } from '~/contexts';
import { DeviceCommandKind, DeviceKind } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { playerFeatureMinimums, playerSupports } from '~/pages/devices/lib/player-version';
import {
  BrightnessDialog,
  PlayOverlayDialog,
  RemoteControlDialog,
  SamsungFirmwareDialog,
  TriggerViewDialog,
  TuneChannelDialog,
  UpdateSoftwareDialog,
  VolumeDialog,
  WebosFirmwareDialog,
} from '../../components';
import { deviceCapabilitiesFor } from '../../lib/capabilities';
import { withTooltip } from '../../lib/withTooltip';
import { PlayContentDialog } from '../../list/components';
import { initialCommands as baseCommands, type Command } from '../lib';
import type { Device } from '../types';

interface CommandsProps {
  device: Device;
}

export const Commands = ({ device }: CommandsProps) => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const [confirmCommand, confirmCommandProps] = useConfirmDialog();
  const [selectedCommand, setSelectedCommand] = useState<Command>();
  const [sendCommand] = useSendDeviceCommand();

  const [brightnessDialogOpen, setBrightnessDialogOpen] = useState(false);
  const [volumeDialogOpen, setVolumeDialogOpen] = useState(false);
  const [tuneChannelDialogOpen, setTuneChannelDialogOpen] = useState(false);
  const [remoteControlDialogOpen, setRemoteControlDialogOpen] = useState(false);
  const [playContentDialogOpen, setPlayContentDialogOpen] = useState(false);
  const [playOverlayDialogOpen, setPlayOverlayDialogOpen] = useState(false);
  const [updateSoftwareDialogOpen, setUpdateSoftwareDialogOpen] = useState(false);
  const [triggerViewDialogOpen, setTriggerViewDialogOpen] = useState(false);
  const [samsungDialogOpen, setSamsungDialogOpen] = useState(false);
  const [webosDialogOpen, setWebosDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);

  const handleFirmwareInstall = useCallback(
    async (cmd: Command) => {
      await sendCommand({
        variables: {
          input: {
            command: cmd.command,
            selected: { ids: [device.id] },
            payload: cmd.payload ?? {},
          },
        },
      });
    },
    [device.id, sendCommand],
  );

  const selected = useMemo(() => ({ ids: [device.id] }), [device.id]);

  const caps = deviceCapabilitiesFor(device);

  const commands: Command[] = useMemo(() => {
    const inactiveEnvs = [...new Set(device.inactiveEnvironments)];

    return [
      ...baseCommands,
      ...(currentUser.admin
        ? [
            {
              Icon: CloudUpload,
              command: DeviceCommandKind.Logs,
              description: t('pages.devices.show.commands.upload_logs_description'),
              title: t('pages.devices.show.commands.upload_logs'),
            },
            ...(caps.analyzeChannels
              ? [
                  {
                    Icon: Analytics,
                    command: DeviceCommandKind.AnalyzeChannels,
                    description: t('pages.devices.show.commands.analyze_channels_description'),
                    title: t('pages.devices.show.commands.analyze_channels'),
                  },
                ]
              : []),
            ...inactiveEnvs.map((x) => ({
              Icon: SwapHoriz,
              command: DeviceCommandKind.Environment,
              description: t('pages.devices.show.commands.move_to_description', { environment: x }),
              payload: { environment: x },
              title: `${t('pages.devices.show.commands.move_to_environment')} ${
                x.charAt(0).toUpperCase() + x.slice(1)
              }`,
            })),
          ]
        : []),
    ];
  }, [caps.analyzeChannels, currentUser, device, t]);

  const handleClick = useCallback(
    async (command: Command) => {
      setSelectedCommand(command);
      if (!(await confirmCommand())) return;
      await sendCommand({
        variables: {
          input: {
            command: command.command,
            selected,
            payload: command.payload || {},
          },
        },
      });
    },
    [confirmCommand, selected, sendCommand],
  );

  if (!device.canUpdate.value) return null;

  // Compute disabled reasons once so the tooltip text matches the popover.
  const realtimeReason = !device.realtime
    ? t('pages.devices.bulk_actions.disabled_reasons.not_realtime')
    : undefined;
  const playViewReason = !device.activeShow
    ? t('pages.devices.bulk_actions.disabled_reasons.no_active_show')
    : device.activeShow.show.viewCount < 2
    ? t('pages.devices.bulk_actions.disabled_reasons.single_view_show')
    : undefined;
  const tuneChannelReason = !device.activeShow?.show.channelGuide?.channelCount
    ? t('pages.devices.bulk_actions.disabled_reasons.no_channel_guide')
    : undefined;
  // The version gate comes first: telling someone to enable realtime mode on a
  // player that can't do remote control at all would be a dead end.
  const remoteControlReason = !playerSupports(device.playerVersion, 'remoteControl')
    ? t('pages.devices.bulk_actions.disabled_reasons.player_version_required', {
        version: playerFeatureMinimums.remoteControl,
      })
    : realtimeReason;
  const updateSoftwareReason = !device.canUpdateSoftware.value
    ? t('pages.devices.bulk_actions.disabled_reasons.software_up_to_date')
    : undefined;
  const samsungFirmwareReason =
    device.metadata == null || !('model' in device.metadata) || !('firmware' in device.metadata)
      ? t('pages.devices.bulk_actions.disabled_reasons.firmware_metadata_missing')
      : undefined;
  const webosFirmwareReason =
    device.metadata == null ||
    !('modelName' in device.metadata) ||
    !('firmwareVersion' in device.metadata)
      ? t('pages.devices.bulk_actions.disabled_reasons.firmware_metadata_missing')
      : undefined;

  return (
    <>
      <SettingsHead>{t('pages.devices.show.commands.commands_heading')}</SettingsHead>
      <SettingsBody>
        <SettingsData className="last scroll no-space-between">
          {commands.map((command) => (
            <VerticalButton
              Icon={command.Icon}
              key={command.title}
              onClick={async () => await handleClick(command)}
              title={command.title}
            />
          ))}
          {withTooltip(
            <VerticalButton
              Icon={Layers}
              disabled={Boolean(realtimeReason)}
              onClick={() => setPlayOverlayDialogOpen(true)}
              title={t('pages.devices.show.commands.play_overlay')}
            />,
            realtimeReason,
          )}
          {withTooltip(
            <VerticalButton
              Icon={StopCircle}
              disabled={Boolean(realtimeReason)}
              onClick={() =>
                void handleClick({
                  Icon: StopCircle,
                  command: 'STOP_OVERLAY',
                  description: t('pages.devices.show.commands.stop_overlay_description'),
                  title: t('pages.devices.show.commands.stop_overlay'),
                })
              }
              title={t('pages.devices.show.commands.stop_overlay')}
            />,
            realtimeReason,
          )}
          {withTooltip(
            <VerticalButton
              Icon={PlayArrow}
              disabled={Boolean(playViewReason)}
              onClick={() => setTriggerViewDialogOpen(true)}
              title={t('pages.devices.show.commands.play_view')}
            />,
            playViewReason,
          )}
          <VerticalButton
            Icon={CropOriginal}
            onClick={() => setPlayContentDialogOpen(true)}
            title={t('pages.devices.show.commands.play_content')}
          />
          {withTooltip(
            <VerticalButton
              Icon={LiveTv}
              disabled={Boolean(tuneChannelReason)}
              onClick={() => setTuneChannelDialogOpen(true)}
              title={t('pages.devices.show.commands.tune_channel')}
            />,
            tuneChannelReason,
          )}
          {currentUser.admin &&
            withTooltip(
              <VerticalButton
                Icon={SettingsRemote}
                disabled={Boolean(remoteControlReason)}
                onClick={() => setRemoteControlDialogOpen(true)}
                title={t('pages.devices.show.commands.remote_control')}
              />,
              remoteControlReason,
            )}
          {currentUser.admin && (
            <VerticalButton
              Icon={SwapHoriz}
              onClick={() => setTransferDialogOpen(true)}
              title={t('pages.devices.show.commands.transfer')}
            />
          )}
          {currentNetwork.canManage.value &&
            withTooltip(
              <VerticalButton
                Icon={OfflineBolt}
                disabled={Boolean(updateSoftwareReason)}
                onClick={() => setUpdateSoftwareDialogOpen(true)}
                title={t('pages.devices.show.commands.update_software')}
              />,
              updateSoftwareReason,
            )}
          {device.kind === DeviceKind.Samsung &&
            currentNetwork.canManage.value &&
            withTooltip(
              <VerticalButton
                Icon={InstallDesktop}
                disabled={Boolean(samsungFirmwareReason)}
                onClick={() => setSamsungDialogOpen(true)}
                title={t('pages.devices.show.commands.install_firmware')}
              />,
              samsungFirmwareReason,
            )}
          {device.kind === DeviceKind.Webos &&
            currentNetwork.canManage.value &&
            withTooltip(
              <VerticalButton
                Icon={InstallDesktop}
                disabled={Boolean(webosFirmwareReason)}
                onClick={() => setWebosDialogOpen(true)}
                title={t('pages.devices.show.commands.install_firmware')}
              />,
              webosFirmwareReason,
            )}
          {withTooltip(
            <VerticalButton
              Icon={VolumeUp}
              disabled={!caps.absoluteVolume}
              onClick={() => setVolumeDialogOpen(true)}
              title={t('pages.devices.show.commands.set_volume')}
            />,
            caps.absoluteVolume
              ? undefined
              : t('pages.devices.bulk_actions.disabled_reasons.not_supported'),
          )}
          {withTooltip(
            <VerticalButton
              Icon={Brightness6}
              disabled={!caps.brightness}
              onClick={() => setBrightnessDialogOpen(true)}
              title={t('pages.devices.show.commands.set_brightness')}
            />,
            caps.brightness
              ? undefined
              : t('pages.devices.bulk_actions.disabled_reasons.not_supported'),
          )}
        </SettingsData>
      </SettingsBody>

      <ConfirmDialog
        {...confirmCommandProps}
        cancel={t('common.actions.cancel')}
        confirmButton={
          <Button onClick={confirmCommandProps.onConfirm} startIcon={<Send />} variant="contained">
            {t('common.dialogs.confirm')}
          </Button>
        }
        prompt={<span>{selectedCommand?.description}</span>}
        title={t('pages.devices.show.commands.send_command_title')}
      />

      {brightnessDialogOpen && (
        <BrightnessDialog
          onClose={() => setBrightnessDialogOpen(false)}
          onComplete={() => setBrightnessDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {volumeDialogOpen && (
        <VolumeDialog
          onClose={() => setVolumeDialogOpen(false)}
          onComplete={() => setVolumeDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {tuneChannelDialogOpen && (
        <TuneChannelDialog
          channelGuideId={device.activeShow?.show.channelGuide?.id}
          onClose={() => setTuneChannelDialogOpen(false)}
          onComplete={() => setTuneChannelDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {remoteControlDialogOpen && (
        <RemoteControlDialog
          onClose={() => setRemoteControlDialogOpen(false)}
          open
          selected={selected}
          supportsMute={caps.mute}
          supportsVolume={caps.relativeVolume}
        />
      )}
      {playContentDialogOpen && (
        <PlayContentDialog
          onClose={() => setPlayContentDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {playOverlayDialogOpen && (
        <PlayOverlayDialog
          onClose={() => setPlayOverlayDialogOpen(false)}
          onComplete={() => setPlayOverlayDialogOpen(false)}
          open
          selected={selected}
        />
      )}
      {updateSoftwareDialogOpen && (
        <UpdateSoftwareDialog
          onClose={() => setUpdateSoftwareDialogOpen(false)}
          onComplete={() => setUpdateSoftwareDialogOpen(false)}
          selected={selected}
        />
      )}
      {triggerViewDialogOpen && device.activeShow && (
        <TriggerViewDialog
          activeShowId={device.activeShow.show.id}
          currentViewId={device.currentView?.id}
          onClose={() => setTriggerViewDialogOpen(false)}
          selected={selected}
        />
      )}
      {samsungDialogOpen && (
        <SamsungFirmwareDialog
          deviceId={device.id}
          handleClick={handleFirmwareInstall}
          onClose={() => setSamsungDialogOpen(false)}
        />
      )}
      {webosDialogOpen && (
        <WebosFirmwareDialog
          deviceId={device.id}
          handleClick={handleFirmwareInstall}
          onClose={() => setWebosDialogOpen(false)}
        />
      )}
      <TransferDialog device={device} open={transferDialogOpen} setOpen={setTransferDialogOpen} />
    </>
  );
};
